import useLoadIntegrations from '@/connection/hooks/use-load-integrations';
import { ChannelFeaturesEnum } from '@/store/slices/feature-flags/types';
import { getAvailableInstances, getSolutions } from '@/store/slices/integrations/selectors';
import OptionalComponent from '@ui/optional-component/optional-component';
import RSVPLinkCard from './components/rsvp/rsvp-link-card';
import { getActiveChannel } from '@/store/slices/user/selectors';
import { getChannels } from '@/store/slices/channels/selectors';
import IntegrationsEventDetailsCard from './components/tray/integrations-event-details-card';
import { getWorkingProjectUuid } from '@/store/slices/project/selectors';
import { useMemo, useState } from 'react';
import { ESolutionNames, ITraySolution } from '@/types/integrations.types';
import InboundIntegrationCard from './components/inbound/inbound-integration-card';
import SearchInput from '@ui/search-input/search-input';
import { useAppSelector } from '@/store/hooks';

import './integrations.css';

const INBOUND_INTEGRATION_SOLUTIONS = [
  ESolutionNames.Marketo,
  ESolutionNames.Hubspot,
  ESolutionNames.Salesforce,
  ESolutionNames.Custom,
];

const Integrations = () => {
  useLoadIntegrations();

  const solutions = useAppSelector(getSolutions);
  const availableInstances = useAppSelector(getAvailableInstances);
  const activeChannel = useAppSelector(getActiveChannel);
  const channels = useAppSelector(getChannels);
  const projectUuid = useAppSelector(getWorkingProjectUuid);

  const solutionsMap = useMemo(() => {
    return solutions?.reduce((acc: Record<string, ITraySolution>, solution) => {
      acc[solution.id] = solution;
      return acc;
    }, {});
  }, [solutions]);

  const [searchTerm, setSearchTerm] = useState('');

  const rsvpFeatureFlagOn = channels
    ?.find((channel) => channel.channel === activeChannel)
    ?.enabled_features?.includes(ChannelFeaturesEnum.rsvp_integration);

  const filteredInstances = availableInstances.filter((instance) => {
    const instanceName = instance.name.toLowerCase(); // user defined integration name
    const integrationType = solutionsMap[instance?.solution_uuid]?.title.toLowerCase(); // integration type (Salesforce, Marketo, etc)
    return instanceName.includes(searchTerm) || integrationType.includes(searchTerm);
  });
  const filteredInbound = INBOUND_INTEGRATION_SOLUTIONS.filter((inbound) => inbound.toLowerCase().includes(searchTerm));

  const handleSearch = (search: string) => {
    setSearchTerm(search.toLowerCase());
  };

  return (
    <div className={'integrations-view flex-column gap-10'}>
      <div className="flex jc-flex-end">
        <SearchInput onSubmit={handleSearch} debounceTime={300} onClear={() => handleSearch('')} />
      </div>
      <div className="integrations-grid">
        <OptionalComponent display={rsvpFeatureFlagOn}>
          <RSVPLinkCard />
        </OptionalComponent>

        {filteredInstances.map((instance) => {
          const solutionName = solutionsMap[instance?.solution_uuid]?.title;
          if (!projectUuid) return null;
          return (
            <IntegrationsEventDetailsCard
              key={instance.instance_uuid}
              instance={instance}
              projectUuid={projectUuid}
              solutionTitle={solutionName}
            />
          );
        })}

        {/* Inbound integration tiles (static pre defined) */}
        {filteredInbound.map((solution, i) => {
          return <InboundIntegrationCard key={`inbound-${solution}-${i}`} solution={solution} />;
        })}
      </div>
    </div>
  );
};

export default Integrations;
